import { Component, ComponentRef, EventEmitter, inject, Inject, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EmailValidator } from "../../form/validator/email-validator";
import { SimpleSubscriptionRequestInterface } from "../../models/auth";
import { ToastService } from "../../services/toast.service";
import { HttpSubscriptionsService } from "../../services/http/subscriptions.service";
import { PostalAddressInterface } from "../../models/customer";
import { AddressFormComponent } from "../address-form/address-form.component";
import { AuthService } from "../../services/auth.service";
import { scrollToFirstEl } from "../../helpers";
import { SubscriptionSelectionInterface } from "@gea-waldviertler-website/gea-models";
import { EnvService } from "@gea-waldviertler-website/shop-lib";

@Component({
    selector: 'gea-shop-subscriptions-form',
    templateUrl: './subscriptions.component.html',
    standalone: false
})
export class SubscriptionsFormComponent {

  @Output("selectStoreLocation") submitEvent = new EventEmitter<undefined>()

  @ViewChild('addressForm', { read: ComponentRef, static: false }) public addressForm?: ComponentRef<AddressFormComponent>

  form: FormGroup
  subscribeItems: SubscriptionSelectionInterface[]
  captchaIsLoading: boolean = false
  loading: boolean = false
  hasPostal: boolean = false
  postalAddress?: PostalAddressInterface = undefined
  submitClicked: boolean = false
  successfullSubmitted: boolean = false
  invalid: boolean = false

  emailDomainValid?: boolean = undefined
  emailPlausible?: boolean = undefined

  private envService = inject(EnvService)

  constructor(
    @Inject(ToastService) private toastService: ToastService,
    @Inject(HttpSubscriptionsService) private subService: HttpSubscriptionsService,
    @Inject(AuthService) private authService: AuthService,
    fb: FormBuilder
  ) {
    this.subscribeItems = this.envService.config.subscribeItems

    this.form = fb.group({
      firstname: [ "", {validators: [ Validators.required ]} ],
      lastname: [ "", {validators: [ Validators.required ]} ],
      email: [ "", {validators: [ Validators.required, EmailValidator.matchValidator ]} ],
      subscriptions: fb.array<SubscriptionSelectionInterface>([]),
      captchaSolution: [ "", {validators: [ Validators.required ]} ],
    }, {
    })

    for (const sub of this.subscribeItems) {
      this.subscriptions.push(fb.group({
        code: [ sub.code ],
        checked: [ (sub.type == "postal" ? sub.checked : true) ], // email default true, but only in this component
        type: [ sub.type ]
      }))
    }

    this.form.valueChanges.subscribe(data => {
      let hasPostal = false
      data.subscriptions.forEach((sub: SubscriptionSelectionInterface) => {
        if (sub.checked && sub.type == "postal") {
          hasPostal = true
        }
      })

      this.hasPostal = hasPostal
      this.emailPlausible = this.form.controls['email'].valid

      if (this.emailPlausible && data.email) {
        this.authService.checkEmailDomain(data.email).subscribe(response => {
          this.emailDomainValid = response
        })
      } else {
        this.emailDomainValid = undefined
      }
    })
  }

  markAddressFormInvalid() {
    return this.submitClicked && ! this.isPostalAddressValid()
  }

  get subscriptions() {
    return this.form.controls["subscriptions"] as FormArray;
  }

  onAddressChange(event: any) {
    const address = event as PostalAddressInterface
    this.postalAddress = address
    this.invalid = false
  }

  pushCaptchaSolution(v: any) {
    this.form.get('captchaSolution')?.setValue(v)
  }

  pushCaptchaLoadingState(v: any) {
    this.loading = v
  }

  isPostalAddressValid() {
    return this.postalAddress?.street
      && this.postalAddress?.houseNumber && this.postalAddress?.houseNumber.length > 0
      && this.postalAddress?.city && this.postalAddress?.city.length > 0
      && this.postalAddress?.postalcode && this.postalAddress?.postalcode.length > 0
      && this.postalAddress?.country !== undefined
  }

  submit(formValues: SimpleSubscriptionRequestInterface) {
    this.submitClicked = true
    if (this.hasPostal) {
      if (! this.isPostalAddressValid()) {
        this.addressForm?.instance.form?.markAllAsTouched()
        scrollToFirstEl('gea-shop-subscriptions-form p.txt-red')
        this.invalid = true
        return
      } else {
        formValues.address = {
          street: this.postalAddress?.street + ' ' + this.postalAddress?.houseNumber,
          city: this.postalAddress?.city + '',
          postalcode: this.postalAddress?.postalcode + '',
          country: this.postalAddress?.country?.name + ''
        }
        this.submitClicked = false
        this.invalid = false
      }
    }
    this.subService.subscribe(formValues).subscribe(user => {
      this.toastService.showSuccess('Du hast dich erfolgreich am Newsletter angemeldet!')
      this.successfullSubmitted = true
      this.submitEvent.emit()
    }, error => {
      this.toastService.showError('Bei der Newsletter-Anmeldung ist ein Fehler aufgetreten.')
    })
  }
}
